import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Instrument } from "../../sdk";
import { Link } from "../../components/Link";
import { CoinMark } from "../../components/ui";
import { useAssetCtx } from "../../hl/hooks";
import { useMark, useTradingState, type Mark } from "../../state/market";
import { big, changeBp, fromRaw, pctBp, price, scalePlaces, tone } from "../../lib/num";

/** The price with the digits that just changed lit in the colour of the move. */
function LivePrice({ mark, scale }: { mark: Mark | undefined; scale: string }) {
  const text = mark ? price(mark.raw, scale) : "—";
  const [lit, setLit] = useState<{ from: number; dir: "up" | "down"; key: number } | null>(null);
  const last = useRef<{ raw: string; text: string } | null>(null);
  useEffect(() => {
    if (!mark) return;
    const previous = last.current;
    last.current = { raw: mark.raw, text };
    if (!previous || previous.raw === mark.raw) return;
    let from = 0;
    while (from < text.length && text[from] === previous.text[from]) from += 1;
    setLit({ from, dir: big(mark.raw) > big(previous.raw) ? "up" : "down", key: Date.now() });
  }, [mark, text]);
  const from = lit ? Math.min(lit.from, text.length) : text.length;
  return (
    <span className="mh-price num" aria-live="off">
      {text.slice(0, from)}
      {from < text.length && (
        <span key={lit?.key} className={`mh-lit mh-lit-${lit?.dir ?? "up"}`}>
          {text.slice(from)}
        </span>
      )}
    </span>
  );
}

/** Why the table is not taking calls, when it is not. */
function Banner({ instrument }: { instrument: Instrument }) {
  const state = useTradingState().data;
  const banner = state?.tradingPaused
    ? "Play is paused. Cash outs still settle."
    : state?.pausedActions.includes("openPosition")
      ? "The table is not taking new calls right now."
      : instrument.terminal
        ? `${instrument.symbol} is retired: open calls settle at its last price.`
        : instrument.closeOnly
          ? `${instrument.symbol} is closed to new calls. Cash outs still settle.`
          : null;
  return banner ? <div className="mh-banner">{banner}</div> : null;
}

/** The market switch and its live price, laid over the chart; `aside` sits across from the switch. */
export function MarketHeader({ instrument, instruments, aside }: { instrument: Instrument; instruments: Instrument[]; aside?: ReactNode }) {
  const mark = useMark(instrument.id);
  const ctx = useAssetCtx(instrument.symbol);
  const scale = instrument.priceScale;
  const change = ctx && mark ? changeBp(ctx.prevDayPx, fromRaw(mark.raw, scalePlaces(scale))) : null;

  // the tab says the price
  useEffect(() => {
    if (!mark) return;
    document.title = `${price(mark.raw, scale)} ${instrument.symbol} · playertrade`;
  }, [mark, scale, instrument.symbol]);
  useEffect(() => () => void (document.title = "playertrade"), []);

  return (
    <div className="mh">
      <div className="mh-top">
        <nav className="mh-switch" aria-label="Markets">
          {instruments.map((item) => (
            <Link key={item.id} href={`/play/${item.symbol}`} className="mh-tab" aria-current={item.id === instrument.id ? "page" : undefined}>
              <CoinMark symbol={item.symbol} size={20} />
              {item.symbol}
            </Link>
          ))}
        </nav>
        {aside}
      </div>
      <div className="mh-quote">
        <LivePrice mark={mark} scale={scale} />
        {change !== null && (
          <span className={`mh-change num ${tone(change)}`}>
            {change >= 0n ? "▲" : "▼"} {pctBp(change < 0n ? -change : change)} <span className="faint">today</span>
          </span>
        )}
      </div>
      <Banner instrument={instrument} />
    </div>
  );
}
